import React from 'react';
import { GovIcon } from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { FilterSortDirection } from '@gov-nx/core/types';
import type { PageCode } from '@gov-nx/module/page';
import { useMojePodaniSeznamContextInstance } from '@gov-nx/module/page';
import {
	Button,
	Dialog,
	DSGovFlex,
	FilterParameterList,
	FormDatePicker,
	FormSelect,
	FormWidthElementLayout,
	HeaderLayout,
	LayoutGap,
} from '@gov-nx/ui/web';

export interface MySubmissionsListFilterProps {
	code: PageCode;
}

export const MySubmissionsListFilter = ({
	code,
}: MySubmissionsListFilterProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const {
		controls,
		resetFilter,
		filterParams,
		setControls,
		filter,
		formDefinition,
		onSubmit,
	} = useMojePodaniSeznamContextInstance();

	return (
		<GovFormWeb
			formDefinition={formDefinition}
			onSubmit={onSubmit}>
			<>
				<HeaderLayout className="flex justify-end gap-4">
					<FilterParameterList
						items={filterParams}
						onReset={() => resetFilter()}
						className="pt-1!"
					/>
					<Button
						onClick={() => setControls({ displayFilter: true })}
						color="primary"
						type="outlined"
						aria-label={ts('akce.wcag.zobrazit-filtr')}>
						<GovIcon
							name="funnel"
							type="templates"
						/>
					</Button>
				</HeaderLayout>
				<Dialog
					isOpen={controls.displayFilter}
					onClose={() => setControls({ displayFilter: false })}
					title={<h3>{ts('filtr.nazev')}</h3>}
					footer={
						<>
							{filter.lhutaVyrizeni || filter.vyrizeno ? (
								<Button
									onClick={() => resetFilter()}
									color="primary"
									type="outlined"
									size="m"
									aria-label={ts('akce.wcag.zrusit-filtr')}
									isExpandedMobile>
									{ts('akce.zrusit-filtr')}
								</Button>
							) : null}
							<Button
								color="primary"
								type="solid"
								size="m"
								nativeType="submit"
								aria-label={ts('akce.wcag.filtrovat')}
								isExpandedMobile>
								{ts('akce.filtrovat')}
							</Button>
						</>
					}
					accessibleCloseLabel={ts('akce.wcag.zavrit-filtr')}>
					<LayoutGap gap="m">
						<DSGovFlex gap="l">
							<FormWidthElementLayout>
								<FormDatePicker
									field={{
										name: 'lhutaVyrizeni',
									}}
									label={{
										children: ts('filtr.pole.lhuta-vyrizeni'),
									}}
								/>
							</FormWidthElementLayout>
							<FormWidthElementLayout>
								<FormDatePicker
									field={{
										name: 'vyrizeno',
									}}
									label={{
										children: ts('filtr.pole.vyrizeno'),
									}}
								/>
							</FormWidthElementLayout>
						</DSGovFlex>
						<DSGovFlex gap="l">
							<FormWidthElementLayout>
								<FormSelect
									field={{
										name: 'razeni',
										children: (
											<>
												<option value="datumOdeslani">
													{ts('formular.select.datum-odeslani')}
												</option>
												<option value="datumVyrizeni">
													{ts('formular.select.datum-vyrizeni')}
												</option>
												<option value="lhutaVyrizeni">
													{ts('formular.select.lhuta-vyrizeni')}
												</option>
											</>
										),
									}}
									label={{
										children: ts('filtr.pole.razeni'),
									}}
								/>
							</FormWidthElementLayout>
							<FormWidthElementLayout>
								<FormSelect
									field={{
										name: 'razeniSmer',
										children: (
											<>
												<option value={FilterSortDirection.DESC}>
													{ts('formular.select.sestupne')}
												</option>
												<option value={FilterSortDirection.ASC}>
													{ts('formular.select.vzestupne')}
												</option>
											</>
										),
									}}
									label={{
										children: ts('filtr.pole.razeni-smer'),
									}}
								/>
							</FormWidthElementLayout>
						</DSGovFlex>
					</LayoutGap>
				</Dialog>
			</>
		</GovFormWeb>
	);
};
